import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchRedirects, logRedirectHit } from "../lib/api";

/**
 * Iter-190.A Sprint 6 Phase A — Client-side RedirectGuard.
 *
 * Fetches the active redirect rule set once on app boot, then on every
 * location change checks whether the current path matches a source.
 * If it does:
 *   - 301 / 302  → `window.location.replace()` to the target (browsers
 *                  treat this as a hard navigation, dropping the source
 *                  from history exactly as a server-side 301 would).
 *   - 410        → render a "This page is no longer available" panel
 *                  with a `noindex` meta tag (so search engines drop
 *                  the URL from their index over time).
 *
 * Caveat documented in `/app/backend/redirects_cms.py`: this is NOT a
 * true server-side 301 — the frontend pod still returns 200 on the
 * source path. For SEO juice, copy the rule set into Cloudflare /
 * Vercel / Nginx at the edge. The admin CMS export is at
 * `GET /api/redirects` (JSON) which the user can adapt to a
 * `_redirects` file or similar.
 */
const STORAGE_KEY = "carsavr.redirects.v1";
const TTL_MS = 5 * 60 * 1000;

function loadCache() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed.rules;
  } catch {
    return null;
  }
}

function saveCache(rules) {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ts: Date.now(), rules }),
    );
  } catch {
    /* swallow */
  }
}

export default function RedirectGuard() {
  const location = useLocation();
  const [rules, setRules] = useState(() => loadCache() || []);
  const [goneRule, setGoneRule] = useState(null);

  // Fetch once on mount
  useEffect(() => {
    let alive = true;
    fetchRedirects()
      .then((res) => {
        if (!alive) return;
        const list = res?.rules || [];
        setRules(list);
        saveCache(list);
      })
      .catch(() => {
        /* swallow — fall back to empty list */
      });
    return () => {
      alive = false;
    };
  }, []);

  // Check on every route change
  useEffect(() => {
    if (!rules.length) return;
    // Normalize current path the same way backend does (strip trailing /
    // except for the root)
    let path = location.pathname || "/";
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);

    const rule = rules.find((r) => r.source_path === path);
    if (!rule) {
      setGoneRule(null);
      return;
    }
    logRedirectHit(rule.source_path);

    if (rule.status_code === 410) {
      setGoneRule(rule);
      return;
    }
    if (rule.target_path) {
      // Preserve the query string the visitor arrived with
      const search = window.location.search || "";
      const hash = window.location.hash || "";
      const target = rule.target_path.startsWith("http")
        ? rule.target_path
        : rule.target_path + search + hash;
      window.location.replace(target);
    }
  }, [location.pathname, rules]);

  if (goneRule) {
    return <GonePanel rule={goneRule} />;
  }
  return null;
}

function GonePanel({ rule }) {
  return (
    <div
      data-testid="redirect-gone-panel"
      className="fixed inset-0 z-[200] bg-white grid place-items-center px-6"
    >
      {/* Noindex tag so crawlers drop this URL from their index. We
          can't return a true 410 from the React layer, but the next
          best signal is the meta tag + the user-visible "gone" UI. */}
      <meta name="robots" content="noindex" />
      <div className="max-w-md text-center">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981] mb-2">
          Iter-190.A Sprint 6
        </p>
        <h1 className="font-display font-black text-4xl text-slate-950 tracking-tight mb-3">
          This page is no longer available.
        </h1>
        <p className="text-slate-600 leading-relaxed mb-6">
          {rule.notes ||
            "The content at this URL has been permanently removed."}
        </p>
        <a
          href="/"
          data-testid="redirect-gone-home-link"
          className="inline-flex items-center gap-2 h-12 px-5 bg-[#0A2540] hover:bg-slate-900 text-white font-bold tracking-tight transition-colors"
        >
          Back to CarSavr home
        </a>
      </div>
    </div>
  );
}
